<script>
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { fade } from 'svelte/transition';
    import { figuresStore } from '../../stores/figuresStore';
    import { API_CONFIG } from '../../config/api';
    import AccessibleModal from '../shared/AccessibleModal.svelte';

    const dispatch = createEventDispatcher();

    export let show = false;

    // Auto-refresh interval (in milliseconds)
    const AUTO_REFRESH_INTERVAL = 5000; // 5 seconds
    let refreshInterval = null;

    // Subscribe to the figures store for single source of truth
    $: ({ figures, loading, error } = $figuresStore);

    // Sort figures alphabetically by name with natural number sorting
    $: sortedFigures = (figures || []).sort((a, b) => {
        const nameA = a.name || 'Untitled Figure';
        const nameB = b.name || 'Untitled Figure';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });

    // Load figures and start auto-refresh when modal opens, stop when closed
    $: if (show) {
        figuresStore.loadFigures(false, true); // silent load
        startAutoRefresh();
    } else {
        stopAutoRefresh();
    }

    function startAutoRefresh() {
        if (refreshInterval) return; // Already running
        refreshInterval = setInterval(() => {
            figuresStore.silentRefresh();
        }, AUTO_REFRESH_INTERVAL);
    }

    function stopAutoRefresh() {
        if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
        }
    }

    // Clean up interval on component destroy
    onDestroy(() => {
        stopAutoRefresh();
    });

    function refreshFigures() {
        // Force refresh to get latest data from Patdraw (silently)
        figuresStore.silentRefresh();
    }

    function closeModal() {
        show = false;
        dispatch('close');
    }

    function handleInsertFigure(item) {
        // Handle both figures and numerals
        const eventData = {
            id: item.id,
            name: item.name || item.displayName || 'Unnamed',
            type: item.type || 'figure'
        };
        
        // For numerals, also include the value
        if (item.type === 'numeral' && item.value !== undefined) {
            eventData.value = item.value;
        }
        
        dispatch('insert', eventData);
        closeModal();
    }

    function handleOpenPatdraw() {
        // Open Patdraw frontend in a new tab
        // TODO: In production with Electron, this will need to:
        // - Launch the Patdraw Electron app (not just open a URL)
        // - Use IPC or deep linking to communicate between apps
        // - Ensure both frontend and backend are running together
        // For now, this dev implementation assumes backend is already running
        window.open(API_CONFIG.PATDRAW_FRONTEND_URL, '_blank');
    }
</script>

<AccessibleModal 
    bind:show={show}
    variant="spec"
    title="Figures"
    titleId="figure-modal-title"
    on:close={closeModal}
    data-testid="figures-modal"
>
    <div>
        <div class="modal-body">
            <div class="figures-header">
                <h3>Figures & Numerals from Patdraw</h3>
                <div class="header-buttons">
                    <button
                        class="refresh-button"
                        on:click={refreshFigures}
                        aria-label="Refresh figures"
                        title="Sync figure content from Patdraw"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M14 8A6 6 0 1 1 8 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            <path d="M8 5V2h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button class="modal-button modal-button-accent" on:click={handleOpenPatdraw} aria-label="Open Patdraw">
                        Open Patdraw
                    </button>
                </div>
            </div>
            
            <p class="usage-note">
                Figures and numerals are managed in Patdraw. You can insert them here:<br>
                1. Click the "Use" button next to any figure or numeral.<br>
                2. Type @ directly in the spec editor to search all items.
            </p>

            {#if loading && figures.length === 0}
                <div class="loading-state">
                    <div class="loading-spinner"></div>
                    <p>Loading figures from Patdraw...</p>
                </div>
            {:else if error}
                <div class="error-state">
                    <p class="error-message">Failed to load figures</p>
                    <p class="error-detail">{error}</p>
                    <p class="error-help">Make sure Patdraw is running at {API_CONFIG.PATDRAW_API_URL}</p>
                </div>
            {:else if sortedFigures.length === 0}
                <div class="empty-state">
                    <p>No figures available</p>
                    <p class="empty-help">Create figures in Patdraw and they will appear here</p>
                </div>
            {:else}
                <div class="figures-list">
                    {#each sortedFigures as figure (figure.id)}
                        <div class="figure-item" class:numeral-item={figure.type === 'numeral'}>
                            <div class="figure-info">
                                <div class="item-header">
                                    <h4>{figure.displayName || figure.name || 'Untitled'}</h4>
                                    <span class="item-type {figure.type || 'figure'}">{figure.type === 'numeral' ? 'Numeral' : 'Figure'}</span>
                                </div>
                                {#if figure.type === 'numeral'}
                                    <p class="numeral-details">
                                        Value: {figure.value} | Figure: {figure.figureName || 'Unknown'}
                                    </p>
                                {:else if figure.modified}
                                    <p class="date">{new Date(figure.modified).toLocaleDateString()}</p>
                                {/if}
                                {#if figure.imageData || figure.previewUrl}
                                    <img 
                                        src={figure.imageData || `${API_CONFIG.PATDRAW_API_URL}${figure.previewUrl}${figure.previewUrl.includes('?') ? '&' : '?'}t=${Date.now()}`} 
                                        alt={figure.displayName || figure.name} 
                                        class="figure-preview" 
                                        loading="lazy"
                                    />
                                {/if}
                            </div>
                            <div class="figure-actions">
                                <button 
                                    class="modal-button modal-button-accent"
                                    on:click={() => handleInsertFigure(figure)}
                                >
                                    Use
                                </button>
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
    </div>
</AccessibleModal>

<style>
    .modal-body {
        padding: var(--spacing-6);
    }

    .figures-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-4);
    }

    .figures-header h3 {
        margin: 0;
        font-size: 1.2rem;
        color: var(--color-text-primary);
    }

    .usage-note {
        margin: var(--spacing-2) 0 var(--spacing-4);
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
    }

    .header-buttons {
        display: flex;
        gap: var(--spacing-2);
        align-items: center;
    }

    .refresh-button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        padding: 0;
        color: var(--color-text-secondary);
        background: none;
        border: 1px solid var(--color-gray-300);
        border-radius: 50%;
        transition: var(--transition-colors);
        cursor: pointer;
    }
    
    .refresh-button svg {
        width: 16px;
        height: 16px;
    }

    .refresh-button:hover:not(:disabled) {
        color: var(--color-text-primary);
        background: var(--color-bg-accent-30);
        border-color: var(--color-gray-400);
    }

    .refresh-button:active:not(:disabled) {
        transform: scale(0.95);
    }

    .refresh-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    /* Custom button styling for smaller buttons */
    .figure-actions .modal-button {
        padding: 0.4rem var(--spacing-3);
        font-size: var(--font-size-xs);
    }

    .figures-list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }

    .figure-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: var(--spacing-4);
        background-color: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-xl);
    }

    .figure-info {
        flex: 1;
        min-width: 0;
    }

    .item-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-2);
        margin-bottom: var(--spacing-1);
    }

    .figure-info h4 {
        margin: 0;
        font-size: var(--font-size-base);
        color: var(--color-text-primary);
    }

    .item-type {
        padding: 2px 6px;
        font-size: var(--font-size-xs);
        font-weight: 500;
        text-transform: uppercase;
        border-radius: var(--border-radius-md);
        letter-spacing: 0.5px;
    }

    .item-type.figure {
        color: var(--color-spec-figure-text);
        background-color: var(--color-spec-figure-background);
    }

    .item-type.numeral {
        color: var(--color-terms-700);
        background-color: var(--color-terms-100);
    }

    .numeral-details {
        margin: var(--spacing-1) 0 0;
        font-size: var(--font-size-xs);
        color: var(--color-text-secondary);
    }

    .numeral-item {
        border-left: 3px solid var(--color-terms-300);
    }

    .figure-info .date {
        margin: var(--spacing-1) 0 0;
        font-size: 0.8rem;
        color: var(--color-text-secondary);
    }

    .figure-preview {
        max-width: 200px;
        max-height: 150px;
        padding: var(--spacing-2);
        margin-top: var(--spacing-2);
        background-color: var(--color-bg-panel-50);
        border-radius: var(--border-radius-lg);
        object-fit: contain;
    }

    .figure-actions {
        display: flex;
        align-items: flex-start;
        margin-left: var(--spacing-4);
    }

    
    /* Loading state */
    .loading-state {
        padding: var(--spacing-8);
        text-align: center;
    }
    
    .loading-spinner {
        width: 30px;
        height: 30px;
        margin: 0 auto var(--spacing-4);
        border: 3px solid var(--color-gray-300);
        border-top-color: var(--color-spec-figure-text);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    
    .loading-state p {
        margin: 0;
        font-size: 0.9rem;
        color: var(--color-text-secondary);
    }
    
    /* Error state */
    .error-state {
        padding: var(--spacing-6);
        text-align: center;
    }
    
    .error-message {
        margin: 0 0 var(--spacing-2) 0;
        font-weight: var(--font-weight-medium);
        color: var(--color-error, #dc3545);
    }
    
    .error-detail {
        margin: 0 0 var(--spacing-3) 0;
        font-size: 0.875rem;
        color: var(--color-text-secondary);
    }
    
    .error-help {
        margin: 0;
        font-size: 0.875rem;
        color: var(--color-text-secondary);
        font-style: italic;
    }

    /* Empty state */
    .empty-state {
        padding: var(--spacing-8);
        text-align: center;
    }

    .empty-state p {
        margin: 0 0 var(--spacing-2) 0;
        color: var(--color-text-secondary);
    }

    .empty-help {
        font-size: 0.875rem;
        font-style: italic;
    }
</style>